import { useState } from 'react';
import { User, ShieldCheck, KeyRound, ArrowLeft, Loader2 } from 'lucide-react';
import { awsService } from '@/services/api/awsService';

const STEPS = {
  USERNAME: 'username',
  VERIFICATION: 'verification',
};

const CODE_VALIDITY_MINUTES = 5;

const maskEmail = (email) => {
  if (!email) return '***@***.com';
  const [local, domain] = email.split('@');
  if (local.length <= 2) return `${local[0]}***@${domain}`;
  return `${local[0]}${'*'.repeat(local.length - 2)}${local[local.length - 1]}@${domain}`;
};

const generateVerificationCode = () => {
  const array = new Uint32Array(1);
  crypto.getRandomValues(array);
  const code = 10000000 + (array[0] % 90000000);
  return String(code);
};

const getCodeExpirationTime = () => {
  const expires = new Date(Date.now() + CODE_VALIDITY_MINUTES * 60 * 1000);
  const pad = (n) => String(n).padStart(2, '0');
  return `${expires.getUTCFullYear()}-${pad(expires.getUTCMonth() + 1)}-${pad(expires.getUTCDate())} ${pad(expires.getUTCHours())}:${pad(expires.getUTCMinutes())}:${pad(expires.getUTCSeconds())} UTC`;
};

const ADSelfService = () => {
  const [step, setStep] = useState(STEPS.USERNAME);
  const [username, setUsername] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [generatedCode, setGeneratedCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);

  const handleValidateUser = async (e) => {
    e.preventDefault();
    if (!username.trim()) return;

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const results = await awsService.searchADUsers(username.trim());

      if (!results || results.length === 0) {
        setMessage({ type: 'error', text: 'Username not found. Please verify and try again.' });
        return;
      }

      const user = results.find(
        (u) => u.SamAccountName?.toLowerCase() === username.trim().toLowerCase()
      );

      if (!user) {
        setMessage({ type: 'error', text: 'Username not found. Please verify and try again.' });
        return;
      }

      if (!user.Enabled) {
        setMessage({ type: 'error', text: 'This account is disabled. Please contact the CSS team at teamcss@lla.com or via https://ots.lla.com/.' });
        return;
      }

      if (!user.EmailAddress) {
        setMessage({ type: 'error', text: 'No email address registered for this account. Please contact IT support.' });
        return;
      }

      const code = generateVerificationCode();
      const expiresAt = getCodeExpirationTime();

      setUserEmail(user.EmailAddress);
      setGeneratedCode(code);

      await awsService.sendVerificationCode({
        UserEmail: user.EmailAddress,
        VerificationCode: code,
        MinutesValid: String(CODE_VALIDITY_MINUTES),
      });

      setStep(STEPS.VERIFICATION);
      setMessage({
        type: 'success',
        text: `A verification code was sent to ${maskEmail(user.EmailAddress)}. The code is valid for ${CODE_VALIDITY_MINUTES} minutes (until ${expiresAt}).`,
      });
    } catch {
      setMessage({
        type: 'error',
        text: 'An error occurred validating your account. Please try again or contact Cloud Solutions Support Team.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!verificationCode.trim()) return;

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      if (verificationCode.trim() !== generatedCode) {
        setMessage({ type: 'error', text: 'Invalid or expired code. Please try again.' });
        return;
      }

      setMessage({ type: 'info', text: 'Resetting your password, please wait...' });

      const requestBody = {
        PasswordsToChange: [{ user: username.trim(), sr: '' }],
        RequestedBy: userEmail || 'ces@lla.com',
      };

      const response = await awsService.changeUserPassword(requestBody);

      if (!response['execution-id']) {
        throw new Error('No execution ID received from server');
      }

      const maxAttempts = 15;
      const interval = 2000;
      let pollSuccess = false;

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const result = await awsService.getExecutionResult(response['execution-id']);

        if (result['execution-error-message']) {
          const errorMsg = result['execution-error-message'];
          if (errorMsg.includes('NoSuchKey') || errorMsg.includes('does not exist')) {
            await new Promise((resolve) => setTimeout(resolve, interval));
            continue;
          }
          throw new Error(errorMsg);
        }

        if (result['execution-status-code'] === 200) {
          pollSuccess = true;
          break;
        } else if (result['execution-status-code'] === 202) {
          await new Promise((resolve) => setTimeout(resolve, interval));
          continue;
        } else {
          throw new Error(result['execution-error-message'] || 'Execution failed');
        }
      }

      if (!pollSuccess) {
        throw new Error('The operation is taking longer than expected. Please try again later.');
      }

      setPasswordResetSuccess(true);
      setMessage({
        type: 'success',
        text: 'Your password has been reset successfully. A new password was sent to your registered email address.',
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.message || 'An error occurred resetting your password. Please try again or contact IT support.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep(STEPS.USERNAME);
    setVerificationCode('');
    setGeneratedCode('');
    setMessage({ type: '', text: '' });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-900 to-[#F26522]/20 p-4">
      <div className="relative w-full max-w-md rounded-2xl shadow-2xl animate-fade-in">
        <div className="relative bg-gray-900 rounded-2xl p-8 sm:p-10 flex flex-col justify-center overflow-hidden">
          {/* Decorative circles */}
          <div className="absolute -top-10 -right-10 w-44 h-44 rounded-full bg-[#F26522]/20 pointer-events-none" />
          <div className="absolute -bottom-8 -left-8 w-36 h-36 rounded-full bg-[#F26522]/15 pointer-events-none" />

          {/* Header */}
          <div className="flex flex-col items-center mb-8">
            <img
              src="/LogoLiberty.png"
              alt="Liberty Latin America"
              className="h-25 mb-4 brightness-0 invert"
            />
            <p className="text-xl font-bold text-[#F26522] mt-1">
              Cloud Engineering Services Portal
            </p>
            <p className="text-sm text-gray-400 mt-1">Account Access Center</p>
          </div>

          <div className="space-y-5">
            {step === STEPS.USERNAME && (
              <>
                <div className="text-sm text-gray-400 space-y-2 text-justify">
                  <p>
                    Enter your username to begin the password reset process. If your account exists,
                    you will receive an email with further instructions.
                  </p>
                  <p>
                    If you have any issues receiving the access information please do not hesitate to
                    contact Cloud Solutions Support Team via{' '}
                    <a
                      href="https://ots.lla.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#F26522] hover:underline"
                    >
                      https://ots.lla.com
                    </a>
                  </p>
                  <div className="text-xs text-amber-300/80 bg-amber-900/20 border border-amber-700/30 rounded-lg p-2.5">
                    <strong>Note:</strong> For your security, never share your credentials or reset links
                    with anyone. Our staff will never ask for your password.
                  </div>
                </div>

                <form onSubmit={handleValidateUser} className="space-y-4">
                  <div className="space-y-1.5">
                    <label htmlFor="username" className="block text-sm font-medium text-gray-100">
                      Username
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                        <User size={16} />
                      </span>
                      <input
                        id="username"
                        name="username"
                        type="text"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        placeholder="Enter your username"
                        className="w-full bg-gray-100 border border-gray-300 text-gray-900 rounded-lg pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#F26522] focus:border-transparent transition-all placeholder:text-gray-500"
                        required
                      />
                    </div>
                  </div>

                  {message.text && (
                    <div
                      role="alert"
                      className={`text-sm rounded-lg p-3 ${
                        message.type === 'success'
                          ? 'text-green-300 bg-green-900/30 border border-green-800'
                          : 'text-red-400 bg-red-900/30 border border-red-800'
                      }`}
                    >
                      {message.text}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading || !username.trim()}
                    className="w-full bg-gray-600 hover:bg-[#F26522] text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        <span>Validating...</span>
                      </>
                    ) : (
                      'Continue'
                    )}
                  </button>
                </form>
              </>
            )}

            {step === STEPS.VERIFICATION && (
              <>
                <h2 className="text-lg font-semibold text-gray-200 text-center">
                  {passwordResetSuccess ? 'Password Reset Complete' : 'Verification Code'}
                </h2>

                {message.text && (
                  <div
                    role="alert"
                    className={`text-sm rounded-lg p-3 ${
                      message.type === 'success'
                        ? 'text-green-300 bg-green-900/30 border border-green-800'
                        : message.type === 'info'
                        ? 'text-amber-300/80 bg-amber-900/20 border border-amber-700/30'
                        : 'text-red-400 bg-red-900/30 border border-red-800'
                    }`}
                  >
                    {message.text}
                  </div>
                )}

                {!passwordResetSuccess && !loading && (
                  <form onSubmit={handleVerifyCode} className="space-y-4">
                    <div className="space-y-1.5">
                      <label htmlFor="code" className="block text-sm font-medium text-gray-300">
                        Verification Code
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                          <ShieldCheck size={16} />
                        </span>
                        <input
                          id="code"
                          name="code"
                          type="text"
                          value={verificationCode}
                          onChange={(e) => setVerificationCode(e.target.value)}
                          placeholder="Enter the 8-digit code"
                          className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#F26522] focus:border-transparent transition-all placeholder:text-gray-500"
                          required
                        />
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={loading || !verificationCode.trim()}
                      className="w-full bg-gray-600 hover:bg-[#F26522] text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <>
                          <Loader2 size={18} className="animate-spin" />
                          <span>Verifying...</span>
                        </>
                      ) : (
                        <>
                          <KeyRound size={16} />
                          <span>Verify Code</span>
                        </>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={handleBack}
                      className="w-full text-gray-400 hover:text-gray-200 text-sm transition-colors flex items-center justify-center gap-1"
                    >
                      <ArrowLeft size={14} />
                      Back to username
                    </button>
                  </form>
                )}

                {passwordResetSuccess && (
                  <a
                    href="/login"
                    className="w-full bg-gray-600 hover:bg-[#F26522] text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 shadow-lg flex items-center justify-center gap-2 text-sm"
                  >
                    Go to Login
                  </a>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ADSelfService;
